import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  BarChart3,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Inbox,
  LogOut,
  Menu,
  RefreshCw,
  Search,
  Users,
  X,
} from "lucide-react";

import "./HubAdmin.css";
import AgendaAdmin from "./AgendaAdmin";


const SOURCES = {
  todos: "Todos os registros",
  nfsb: "Nunca Foi Só Batom",
  maker: "Do Maker à Indústria",
  hub: "Atendimentos HUB",
};


function formatDate(value) {
  if (!value) {
    return "—";
  }

  try {
    return new Intl.DateTimeFormat(
      "pt-BR",
      {
        dateStyle: "short",
        timeStyle: "short",
        timeZone: "America/Rio_Branco",
      }
    ).format(
      new Date(value)
    );
  } catch {
    return value;
  }
}


function humanize(value) {
  const labels = {
    protocolo: "Protocolo",
    nome: "Nome",
    nome_equipe: "Nome da equipe",
    nome_completo: "Nome completo",
    email: "E-mail",
    telefone: "Telefone",
    whatsapp: "WhatsApp",
    cpf: "CPF",
    municipio: "Município",
    status: "Status",
    criado_em: "Data de cadastro",
    atualizado_em: "Última atualização",
    dados: "Dados da inscrição",
    integrantes: "Integrantes",
    minicurriculo: "Mini currículo",
    habilidades: "Habilidades",
    motivacao: "Motivação",
    complementaridade: "Complementaridade",
    familiaridade_tecnica: "Familiaridade técnica",
    interesses_regionais: "Interesses regionais",
    documentos: "Documentos",
    descricao: "Descrição",
    perfil: "Perfil",
    organizacao: "Organização",
    assunto: "Assunto",
    consentimento: "Consentimento",
    fase: "Fase",
    programa: "Programa",
    edital: "Edital",
  };

  if (labels[value]) {
    return labels[value];
  }

  return value
    .replaceAll("_", " ")
    .replaceAll(".", " › ")
    .replace(/\b\w/g, (char) =>
      char.toUpperCase()
    );
}


function RenderValue({
  value,
  depth = 0,
}) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return (
      <span className="admin-empty">
        Não informado
      </span>
    );
  }

  if (typeof value === "boolean") {
    return (
      <span>
        {value ? "Sim" : "Não"}
      </span>
    );
  }

  if (
    typeof value === "string" ||
    typeof value === "number"
  ) {
    return (
      <span className="admin-value">
        {String(value)}
      </span>
    );
  }

  if (Array.isArray(value)) {
    return (
      <div className="admin-array">
        {value.map(
          (item, index) => (
            <div
              className="admin-array-item"
              key={index}
            >
              <div className="admin-array-number">
                {index + 1}
              </div>

              <RenderValue
                value={item}
                depth={depth + 1}
              />
            </div>
          )
        )}
      </div>
    );
  }

  if (typeof value === "object") {
    const entries = Object.entries(value)
      .filter(
        ([key]) =>
          ![
            "_id",
            "ip_origem",
          ].includes(key)
      );

    return (
      <div
        className={
          depth === 0
            ? "admin-detail-root"
            : "admin-object"
        }
      >
        {entries.map(
          ([key, item]) => (
            <div
              className="admin-field"
              key={key}
            >
              <div className="admin-field-label">
                {humanize(key)}
              </div>

              <div className="admin-field-value">
                <RenderValue
                  value={item}
                  depth={depth + 1}
                />
              </div>
            </div>
          )
        )}
      </div>
    );
  }

  return (
    <span>
      {String(value)}
    </span>
  );
}


async function api(
  path,
  options = {}
) {
  const response = await fetch(
    path,
    {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    }
  );

  let data = {};

  try {
    data = await response.json();
  } catch {
    data = {};
  }

  if (!response.ok) {
    const error = new Error(
      data.detail ||
      "Não foi possível concluir a operação."
    );

    error.status = response.status;

    throw error;
  }

  return data;
}


function Login({
  onLogin,
}) {
  const [usuario, setUsuario] = useState("");
  const [senha, setSenha] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  async function submit(event) {
    event.preventDefault();

    setError("");
    setSending(true);

    try {
      const data = await api(
        "/api/admin/login",
        {
          method: "POST",
          body: JSON.stringify({
            usuario,
            senha,
          }),
        }
      );

      onLogin(data);

    } catch (err) {
      setError(
        err.message
      );

    } finally {
      setSending(false);
    }
  }

  return (
    <main className="admin-login">
      <section className="admin-login-brand">
        <div className="admin-login-brand-inner">
          <img
            src="/hubacre/logo-vertical.png"
            alt="HUB Acre"
          />

          <span>
            CENTRAL ADMINISTRATIVA
          </span>

          <h1>
            Dados para
            <br />
            <strong>
              acompanhar,
            </strong>
            <br />
            decidir e agir.
          </h1>

          <p>
            Visualize inscrições, atendimentos e
            indicadores do ecossistema HUB Acre.
          </p>
        </div>

        <div className="admin-login-symbol">
          <span />
          <span />
          <span />
          <span />
        </div>
      </section>

      <section className="admin-login-form-area">
        <form
          className="admin-login-form"
          onSubmit={submit}
        >
          <div className="admin-login-title">
            <span>
              ÁREA RESTRITA
            </span>

            <h2>
              Acessar central
            </h2>

            <p>
              Entre com suas credenciais administrativas.
            </p>
          </div>

          <label>
            Usuário
            <input
              autoComplete="username"
              value={usuario}
              onChange={(event) =>
                setUsuario(
                  event.target.value
                )
              }
              required
              autoFocus
            />
          </label>

          <label>
            Senha
            <input
              type="password"
              autoComplete="current-password"
              value={senha}
              onChange={(event) =>
                setSenha(
                  event.target.value
                )
              }
              required
            />
          </label>

          {error && (
            <div className="admin-login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={sending}
          >
            {sending
              ? "Entrando..."
              : "Entrar na central"}
          </button>

          <a
            href="/hubacre"
            className="admin-back-site"
          >
            ← Voltar para o HUB Acre
          </a>
        </form>
      </section>
    </main>
  );
}


function SourceBadge({
  source,
}) {
  return (
    <span
      className={
        `admin-source admin-source-${source}`
      }
    >
      {source === "nfsb" && "Nunca Foi Só Batom"}
      {source === "maker" && "Maker à Indústria"}
      {source === "hub" && "Atendimento HUB"}
    </span>
  );
}


function HubAdmin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const [section, setSection] = useState("dashboard");
  const [mobileMenu, setMobileMenu] = useState(false);

  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const [source, setSource] = useState("todos");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [municipio, setMunicipio] = useState("");
  const [status, setStatus] = useState("");

  const [page, setPage] = useState(1);

  const [records, setRecords] = useState([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [recordsLoading, setRecordsLoading] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    api("/api/admin/me")
      .then(setUser)
      .catch(() =>
        setUser(null)
      )
      .finally(() =>
        setChecking(false)
      );
  }, []);


  const loadDashboard = useCallback(
    async () => {
      setDashboardLoading(true);

      try {
        const data = await api(
          "/api/admin/dashboard"
        );

        setDashboard(data);

      } catch (error) {
        if (error.status === 401) {
          setUser(null);
        }

      } finally {
        setDashboardLoading(false);
      }
    },
    []
  );


  const loadRecords = useCallback(
    async () => {
      setRecordsLoading(true);

      const params = new URLSearchParams({
        origem: source,
        busca: search,
        municipio,
        status,
        pagina: String(page),
        limite: "30",
      });

      try {
        const data = await api(
          `/api/admin/registros?${params.toString()}`
        );

        setRecords(
          data.registros || []
        );

        setRecordsTotal(
          data.total || 0
        );

        setPages(
          data.paginas || 1
        );

      } catch (error) {
        if (error.status === 401) {
          setUser(null);
        }

      } finally {
        setRecordsLoading(false);
      }
    },
    [
      source,
      search,
      municipio,
      status,
      page,
    ]
  );


  useEffect(() => {
    if (!user) {
      return;
    }

    loadDashboard();
  }, [
    user,
    loadDashboard,
  ]);


  useEffect(() => {
    if (
      !user ||
      section !== "registros"
    ) {
      return;
    }

    loadRecords();
  }, [
    user,
    section,
    loadRecords,
  ]);


  async function logout() {
    try {
      await api(
        "/api/admin/logout",
        {
          method: "POST",
        }
      );
    } finally {
      setUser(null);
    }
  }


  async function openDetail(record) {
    setDetailLoading(true);

    try {
      const data = await api(
        `/api/admin/detalhe-completo/${record.source}/${encodeURIComponent(record.protocolo)}`
      );

      setDetail(data);

    } finally {
      setDetailLoading(false);
    }
  }


  function applySearch(event) {
    event.preventDefault();

    setPage(1);
    setSearch(
      searchInput
    );
  }


  function openRecords(nextSource) {
    setSource(nextSource);
    setPage(1);
    setSection("registros");
    setMobileMenu(false);
  }


  const maxMunicipio = useMemo(() => {
    if (
      !dashboard?.municipios?.length
    ) {
      return 1;
    }

    return Math.max(
      ...dashboard.municipios.map(
        (item) => item.total
      )
    );
  }, [dashboard]);


  if (checking) {
    return (
      <div className="admin-loading-screen">
        <RefreshCw className="admin-spin" />
        Carregando central...
      </div>
    );
  }


  if (!user) {
    return (
      <Login
        onLogin={setUser}
      />
    );
  }


  return (
    <main className="hub-admin">

      <aside
        className={
          `admin-sidebar ${
            mobileMenu
              ? "admin-sidebar-open"
              : ""
          }`
        }
      >
        <div className="admin-sidebar-brand">
          <img
            src="/hubacre/logo-vertical.png"
            alt="HUB Acre"
          />

          <div>
            <span>HUB ACRE</span>
            <small>Central administrativa</small>
          </div>
        </div>

        <nav>
          <button
            className={
              section === "dashboard"
                ? "active"
                : ""
            }
            onClick={() => {
              setSection("dashboard");
              setMobileMenu(false);
            }}
          >
            <BarChart3 />
            Visão geral
          </button>

          <button
            className={
              section === "registros" &&
              source === "todos"
                ? "active"
                : ""
            }
            onClick={() =>
              openRecords("todos")
            }
          >
            <Inbox />
            Todos os registros
          </button>

          <div className="admin-sidebar-label">
            GESTÃO
          </div>

          <button
            className={
              section === "agenda"
                ? "active"
                : ""
            }
            onClick={() => {
              setSection("agenda");
              setMobileMenu(false);
            }}
          >
            <CalendarDays />
            Agenda
          </button>

          <div className="admin-sidebar-label">
            PROGRAMAS
          </div>

          <button
            className={
              section === "registros" &&
              source === "nfsb"
                ? "active"
                : ""
            }
            onClick={() =>
              openRecords("nfsb")
            }
          >
            <span className="admin-nav-dot admin-nav-nfsb" />
            Nunca Foi Só Batom
          </button>

          <button
            className={
              section === "registros" &&
              source === "maker"
                ? "active"
                : ""
            }
            onClick={() =>
              openRecords("maker")
            }
          >
            <span className="admin-nav-dot admin-nav-maker" />
            Maker à Indústria
          </button>

          <button
            className={
              section === "registros" &&
              source === "hub"
                ? "active"
                : ""
            }
            onClick={() =>
              openRecords("hub")
            }
          >
            <span className="admin-nav-dot admin-nav-hub" />
            Atendimentos HUB
          </button>
        </nav>

        <div className="admin-sidebar-bottom">
          <div className="admin-sidebar-user">
            <div>
              {(
                user.nome ||
                "A"
              ).charAt(0)}
            </div>

            <span>
              <strong>
                {user.nome}
              </strong>

              <small>
                Administrador
              </small>
            </span>
          </div>

          <button
            className="admin-logout"
            onClick={logout}
          >
            <LogOut />
            Sair
          </button>
        </div>
      </aside>


      {mobileMenu && (
        <button
          className="admin-overlay"
          aria-label="Fechar menu"
          onClick={() =>
            setMobileMenu(false)
          }
        />
      )}


      <section className="admin-main">

        <header className="admin-topbar">
          <button
            className="admin-mobile-menu"
            onClick={() =>
              setMobileMenu(true)
            }
          >
            <Menu />
          </button>

          <div>
            <span>
              HUB ACRE
            </span>

            <strong>
              Central de inscrições e atendimentos
            </strong>
          </div>

          <a href="/hubacre">
            Ver site
          </a>
        </header>


        {section === "dashboard" && (
          <div className="admin-content">

            <div className="admin-page-heading">
              <div>
                <span className="admin-overline">
                  VISÃO GERAL
                </span>

                <h1>
                  Acompanhe o
                  <br />
                  <strong>ecossistema.</strong>
                </h1>

                <p>
                  Todos os registros dos programas e
                  atendimentos em um único lugar.
                </p>
              </div>

              <div className="admin-heading-actions">
                <a
                  href="/api/admin/exportar?origem=todos&formato=xlsx"
                  className="admin-primary-button"
                >
                  <FileSpreadsheet />
                  Exportar Excel
                </a>

                <button
                  className="admin-icon-button"
                  onClick={loadDashboard}
                  title="Atualizar"
                >
                  <RefreshCw />
                </button>
              </div>
            </div>


            {dashboardLoading && !dashboard ? (
              <div className="admin-loading-card">
                <RefreshCw className="admin-spin" />
                Carregando indicadores...
              </div>
            ) : (
              <>
                <div className="admin-kpis">

                  <button
                    className="admin-kpi admin-kpi-total"
                    onClick={() =>
                      openRecords("todos")
                    }
                  >
                    <span>
                      Todos os registros
                    </span>

                    <strong>
                      {dashboard?.totais?.geral || 0}
                    </strong>

                    <small>
                      Base consolidada
                    </small>
                  </button>

                  <button
                    className="admin-kpi"
                    onClick={() =>
                      openRecords("nfsb")
                    }
                  >
                    <span>
                      Nunca Foi Só Batom
                    </span>

                    <strong>
                      {dashboard?.totais?.nfsb || 0}
                    </strong>

                    <small>
                      Inscrições
                    </small>
                  </button>

                  <button
                    className="admin-kpi"
                    onClick={() =>
                      openRecords("maker")
                    }
                  >
                    <span>
                      Maker à Indústria
                    </span>

                    <strong>
                      {dashboard?.totais?.maker || 0}
                    </strong>

                    <small>
                      Inscrições
                    </small>
                  </button>

                  <button
                    className="admin-kpi"
                    onClick={() =>
                      openRecords("hub")
                    }
                  >
                    <span>
                      Atendimentos HUB
                    </span>

                    <strong>
                      {dashboard?.totais?.hub || 0}
                    </strong>

                    <small>
                      Solicitações
                    </small>
                  </button>

                </div>


                <div className="admin-dashboard-grid">

                  <section className="admin-panel">
                    <div className="admin-panel-title">
                      <div>
                        <span>
                          TERRITÓRIO
                        </span>

                        <h2>
                          Registros por município
                        </h2>
                      </div>
                    </div>

                    <div className="admin-city-list">
                      {dashboard?.municipios?.length ? (
                        dashboard.municipios.map(
                          (item) => (
                            <div
                              className="admin-city"
                              key={item.municipio}
                            >
                              <div>
                                <span>
                                  {item.municipio}
                                </span>

                                <strong>
                                  {item.total}
                                </strong>
                              </div>

                              <div className="admin-city-track">
                                <div
                                  style={{
                                    width:
                                      `${
                                        Math.max(
                                          4,
                                          (
                                            item.total /
                                            maxMunicipio
                                          ) * 100
                                        )
                                      }%`
                                  }}
                                />
                              </div>
                            </div>
                          )
                        )
                      ) : (
                        <div className="admin-no-data">
                          Ainda não há registros suficientes.
                        </div>
                      )}
                    </div>
                  </section>


                  <section className="admin-panel">
                    <div className="admin-panel-title">
                      <div>
                        <span>
                          MOVIMENTO
                        </span>

                        <h2>
                          Registros recentes
                        </h2>
                      </div>

                      <button
                        onClick={() =>
                          openRecords("todos")
                        }
                      >
                        Ver todos
                      </button>
                    </div>

                    <div className="admin-recent-list">
                      {dashboard?.recentes?.map(
                        (record) => (
                          <button
                            key={
                              `${record.source}-${record.protocolo}`
                            }
                            onClick={() =>
                              openDetail(record)
                            }
                          >
                            <div className="admin-recent-icon">
                              <Users />
                            </div>

                            <div className="admin-recent-info">
                              <strong>
                                {record.nome}
                              </strong>

                              <span>
                                {record.protocolo}
                              </span>

                              <small>
                                {formatDate(
                                  record.criado_em
                                )}
                              </small>
                            </div>

                            <SourceBadge
                              source={record.source}
                            />
                          </button>
                        )
                      )}
                    </div>
                  </section>

                </div>
              </>
            )}
          </div>
        )}


        {section === "agenda" && (
          <AgendaAdmin />
        )}


        {section === "registros" && (
          <div className="admin-content">

            <div className="admin-page-heading admin-records-heading">
              <div>
                <span className="admin-overline">
                  REGISTROS
                </span>

                <h1>
                  {SOURCES[source]}
                </h1>

                <p>
                  {recordsTotal} registro
                  {recordsTotal === 1 ? "" : "s"} encontrado
                  {recordsTotal === 1 ? "" : "s"}.
                </p>
              </div>

              <div className="admin-export-group">
                <a
                  href={`/api/admin/exportar?origem=${source}&formato=xlsx`}
                  className="admin-primary-button"
                >
                  <FileSpreadsheet />
                  Excel
                </a>

                <a
                  href={`/api/admin/exportar?origem=${source}&formato=csv`}
                  className="admin-secondary-button"
                >
                  <Download />
                  CSV
                </a>
              </div>
            </div>


            <section className="admin-filter-card">

              <form
                className="admin-search"
                onSubmit={applySearch}
              >
                <Search />

                <input
                  value={searchInput}
                  onChange={(event) =>
                    setSearchInput(
                      event.target.value
                    )
                  }
                  placeholder="Buscar nome, protocolo, e-mail, telefone ou município..."
                />

                <button type="submit">
                  Buscar
                </button>
              </form>

              <div className="admin-filters">
                <select
                  value={source}
                  onChange={(event) => {
                    setSource(
                      event.target.value
                    );
                    setPage(1);
                  }}
                >
                  {Object.entries(SOURCES).map(
                    ([value, label]) => (
                      <option
                        value={value}
                        key={value}
                      >
                        {label}
                      </option>
                    )
                  )}
                </select>

                <input
                  value={municipio}
                  onChange={(event) => {
                    setMunicipio(
                      event.target.value
                    );
                    setPage(1);
                  }}
                  placeholder="Filtrar município"
                />

                <input
                  value={status}
                  onChange={(event) => {
                    setStatus(
                      event.target.value
                    );
                    setPage(1);
                  }}
                  placeholder="Filtrar status"
                />

                {(search ||
                  municipio ||
                  status) && (
                  <button
                    className="admin-clear-filter"
                    onClick={() => {
                      setSearch("");
                      setSearchInput("");
                      setMunicipio("");
                      setStatus("");
                      setPage(1);
                    }}
                  >
                    <X />
                    Limpar
                  </button>
                )}
              </div>
            </section>


            <section className="admin-table-card">

              {recordsLoading ? (
                <div className="admin-loading-card">
                  <RefreshCw className="admin-spin" />
                  Carregando registros...
                </div>
              ) : records.length ? (
                <>
                  <div className="admin-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Origem</th>
                          <th>Protocolo</th>
                          <th>Nome / equipe</th>
                          <th>Município</th>
                          <th>Contato</th>
                          <th>Data</th>
                          <th>Status</th>
                          <th />
                        </tr>
                      </thead>

                      <tbody>
                        {records.map(
                          (record) => (
                            <tr
                              key={
                                `${record.source}-${record.protocolo}`
                              }
                            >
                              <td>
                                <SourceBadge
                                  source={record.source}
                                />
                              </td>

                              <td>
                                <code>
                                  {record.protocolo}
                                </code>
                              </td>

                              <td>
                                <strong>
                                  {record.nome}
                                </strong>
                              </td>

                              <td>
                                {record.municipio}
                              </td>

                              <td>
                                <div className="admin-contact-cell">
                                  <span>
                                    {record.email || "—"}
                                  </span>

                                  <small>
                                    {record.telefone || ""}
                                  </small>
                                </div>
                              </td>

                              <td>
                                {formatDate(
                                  record.criado_em
                                )}
                              </td>

                              <td>
                                <span className="admin-status">
                                  {humanize(
                                    record.status
                                  )}
                                </span>
                              </td>

                              <td>
                                <button
                                  className="admin-view-button"
                                  onClick={() =>
                                    openDetail(record)
                                  }
                                >
                                  Visualizar
                                </button>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>


                  <div className="admin-pagination">
                    <span>
                      Página {page} de {pages}
                    </span>

                    <div>
                      <button
                        disabled={page <= 1}
                        onClick={() =>
                          setPage(
                            (value) =>
                              Math.max(
                                1,
                                value - 1
                              )
                          )
                        }
                      >
                        <ChevronLeft />
                        Anterior
                      </button>

                      <button
                        disabled={page >= pages}
                        onClick={() =>
                          setPage(
                            (value) =>
                              Math.min(
                                pages,
                                value + 1
                              )
                          )
                        }
                      >
                        Próxima
                        <ChevronRight />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="admin-empty-state">
                  <Inbox />

                  <strong>
                    Nenhum registro encontrado
                  </strong>

                  <p>
                    Tente alterar os filtros ou o termo de busca.
                  </p>
                </div>
              )}

            </section>
          </div>
        )}

      </section>


      {(detail || detailLoading) && (
        <div className="admin-detail-overlay">

          <button
            className="admin-detail-backdrop"
            aria-label="Fechar"
            onClick={() =>
              setDetail(null)
            }
          />

          <aside className="admin-detail-drawer">

            {detailLoading && !detail ? (
              <div className="admin-loading-card">
                <RefreshCw className="admin-spin" />
                Carregando ficha...
              </div>
            ) : detail && (
              <>
                <header>
                  <div>
                    <SourceBadge
                      source={detail.source}
                    />

                    <h2>
                      {detail.nome}
                    </h2>

                    <code>
                      {detail.protocolo}
                    </code>
                  </div>

                  <button
                    onClick={() =>
                      setDetail(null)
                    }
                  >
                    <X />
                  </button>
                </header>

                <div className="admin-detail-summary">
                  <div>
                    <span>
                      Município
                    </span>

                    <strong>
                      {detail.municipio}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Data
                    </span>

                    <strong>
                      {formatDate(
                        detail.criado_em
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Status
                    </span>

                    <strong>
                      {humanize(
                        detail.status
                      )}
                    </strong>
                  </div>
                </div>

                <div className="admin-detail-actions">
                  <a
                    href={`/api/admin/exportar-registro/${detail.source}/${encodeURIComponent(detail.protocolo)}`}
                    className="admin-primary-button"
                  >
                    <FileSpreadsheet />
                    Baixar esta inscrição (.xlsx)
                  </a>
                </div>

                <div className="admin-detail-content">

                  <section className="admin-detail-section">
                    <div className="admin-detail-section-heading">
                      <div>
                        <span>FORMULÁRIO</span>
                        <h3>Todas as respostas</h3>
                      </div>
                    </div>

                    <RenderValue
                      value={detail.dados}
                    />
                  </section>

                  {detail.documentos?.length > 0 && (
                    <section className="admin-detail-section">
                      <div className="admin-detail-section-heading">
                        <div>
                          <span>ARQUIVOS</span>
                          <h3>Documentos e anexos</h3>
                        </div>

                        <small>
                          {detail.documentos.length} arquivo
                          {detail.documentos.length === 1 ? "" : "s"}
                        </small>
                      </div>

                      <div className="admin-attachments">
                        {detail.documentos.map((documento) => (
                          <div
                            className="admin-attachment"
                            key={documento.chave}
                          >
                            <div className="admin-attachment-info">
                              <strong>
                                {documento.label}
                              </strong>

                              <span>
                                {documento.nome_original}
                              </span>

                              <small>
                                {documento.tipo || "Arquivo"}
                                {documento.tamanho
                                  ? ` • ${(documento.tamanho / 1024 / 1024).toFixed(2)} MB`
                                  : ""}
                              </small>
                            </div>

                            <div className="admin-attachment-actions">
                              <a
                                href={documento.preview_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="admin-preview-file"
                              >
                                Visualizar
                              </a>

                              <a
                                href={documento.download_url}
                                className="admin-download-file"
                              >
                                <Download />
                                Baixar
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {detail.avaliacao && (
                    <section className="admin-detail-section">
                      <div className="admin-detail-section-heading">
                        <div>
                          <span>INTERNO</span>
                          <h3>Avaliação</h3>
                        </div>
                      </div>

                      <RenderValue
                        value={detail.avaliacao}
                      />
                    </section>
                  )}

                </div>
              </>
            )}

          </aside>
        </div>
      )}

    </main>
  );
}

export default HubAdmin;
